import request from "src/utils/request";
import { toastify } from "src/utils/toast";
import { setModalState } from "src/redux/actions/modalActions";
import {
  setEtsyListing,
  setEtsyListings,
  setProductSearched,
  setProductsList,
  setProductsMeta,
  appendEtsyListings,
  setLastEtsyOffset,
  setEtsyResyncLoading,
  setEtsyListingsActive,
  setEtsyListingsActiveLoading,
  setEtsyResyncActiveLoading,
  setEtsyListingsInactive,
  setEtsyListingsInactiveLoading,
  setEtsyResyncInactiveLoading,
  setEtsyListingsDraft,
  setEtsyListingsDraftLoading,
  setEtsyResyncDraftLoading,
} from "src/redux/actions/productActions";
import { checkIfImageExist } from "./utils";
import { isApodManagedProduct } from "src/utils/apod";
import { setCatalogDetails } from "src/redux/actions/orderActions";
import { loadingAction, loadingBtnAction } from "src/redux/actions/userActions";

const extractProductsPayload = (res) => {
  const payload = res?.data?.data ?? {};
  const list = Array.isArray(payload)
    ? payload
    : payload.data || payload.products || payload.items || [];
  const rawMeta = payload.meta || payload.pagination || {};
  const meta = {
    ...rawMeta,
    total:
      rawMeta.total ??
      rawMeta.total_count ??
      rawMeta.count ??
      (Array.isArray(list) ? list.length : 0),
  };

  return { list, meta };
};

export const getProductList = async (dispatch, currentPage, itemsPerPage) => {
  dispatch(loadingAction(true));

  try {
    const res = await request.get(
      `/products?page=${currentPage || 1}&&rowsPerPage=${itemsPerPage || 25}`,
    );
    if (res) {
      dispatch(loadingAction(false));
      dispatch(setProductsList(res.data.data.data));
      dispatch(setProductsMeta(res.data.data.meta));
      dispatch(setProductSearched(false));
    }
  } catch (e) {
    dispatch(loadingAction(false));

    if (e?.response?.status === 404) {
      dispatch(setProductsList([]));
    } else {
      toastify("error", e.response.data.message);
    }
  }
};

export const getApodProductList = async (
  dispatch,
  currentPage,
  itemsPerPage,
) => {
  dispatch(loadingAction(true));

  try {
    const res = await request.get(
      `/products/apod?page=${currentPage || 1}&rowsPerPage=${itemsPerPage || 25}`,
    );
    if (res) {
      const { list, meta } = extractProductsPayload(res);
      dispatch(loadingAction(false));
      dispatch(setProductsList(list));
      dispatch(setProductsMeta(meta));
      dispatch(setProductSearched(false));
    }
  } catch (e) {
    dispatch(loadingAction(false));

    if (e?.response?.status === 404) {
      dispatch(setProductsList([]));
      dispatch(setProductsMeta({ total: 0 }));
    } else {
      toastify("error", e.response?.data?.message || "Failed to load APOD products");
    }
  }
};

export const getProductsDetail = async (dispatch, id, { isApod } = {}) => {
  dispatch(loadingAction(true));

  try {
    const res = await request.get(`/products/${id}`);
    if (res) {
      dispatch(loadingAction(false));
      let product =
        res?.data?.data && res?.data?.data?.length ? res?.data?.data[0] : null;
      const isApodProduct = isApod || isApodManagedProduct(product);

      let formattedProduct = {
        id: product.id,
        listing_id: product?.listing_id ? product?.listing_id : null,
        amazon_sku: product?.amazon_sku ? product?.amazon_sku : null,
        cover: checkIfImageExist(product, { isApod: isApodProduct }),
        images: checkIfImageExist(product, { isApod: isApodProduct }),
        uploadedData: product?.graphic_image,
        notecardData: product?.notecard_image,
        name:
          product?.title === "undefined"
            ? product?.catalog?.title
            : product?.title,
        desc: product?.catalog?.description,
        price: product?.price,
        shipPrice: product?.catalog?.shipping_price,
        catalog: product?.catalog,
        no_of_graphics: product?.catalog?.no_of_graphics,
        etsy_product_skus: product?.etsy_product_skus,
        amazon_product_skus: product?.amazon_product_skus,
        breadCrumb: "products",
        ...product,
        ...(isApodProduct ? { is_apod: 1 } : {}),
      };

      dispatch(setCatalogDetails(formattedProduct));
    }
  } catch (e) {
    toastify("error", e?.response?.data?.message);
    dispatch(loadingAction(false));
  }
};

export const switchApodProductToIcp = async (dispatch, productId) => {
  if (!productId) {
    toastify("error", "Product ID is required");
    return false;
  }

  dispatch(loadingBtnAction(true));

  try {
    const res = await request.post("/products/apod/switch", {
      product_id: productId,
    });

    dispatch(loadingBtnAction(false));
    toastify("success", res?.data?.message || "Product switched to ICP");
    return true;
  } catch (e) {
    dispatch(loadingBtnAction(false));
    toastify(
      "error",
      e.response?.data?.message || "Failed to switch product to ICP",
    );
    return false;
  }
};

export const deleteProduct = async (
  dispatch,
  id,
  listingID,
  page,
  rowsPerPage,
  etsyListings,
  currentStatus = null,
  currentStatusListings = null,
) => {
  dispatch(loadingBtnAction(true));
  try {
    const res = await request.delete(`/products/${id}`);

    if (res) {
      dispatch(setModalState(undefined));
      // Fetch current page (page is 0-based, API expects 1-based)
      // If current page becomes empty after deletion, 404 handler will set state to empty array
      const currentPage = page >= 0 ? page + 1 : 1;
      getProductList(dispatch, currentPage, rowsPerPage);
      toastify("success", res.data.message);
      dispatch(loadingBtnAction(false));
      if (listingID) {
        updateEtsyListing(
          dispatch,
          listingID,
          etsyListings,
          currentStatus,
          currentStatusListings,
        );
      }
    }
  } catch (e) {
    toastify("error", e.response.data.message);
    dispatch(loadingBtnAction(false));
  }
};

export const searchProducts = async (dispatch, inputValue) => {
  dispatch(loadingAction(true));
  try {
    const res = await request.get(`/products/search/${inputValue}`);

    if (res) {
      dispatch(loadingAction(false));
      dispatch(setProductsList(res.data.data));
      toastify("success", res.data.message);
      dispatch(setProductSearched(true));
    }
  } catch (e) {
    toastify("error", e.response.data.message);
    dispatch(setProductSearched(true));
    dispatch(loadingAction(false));
  }
};

export const getEtsyListing = async (dispatch, listingid) => {
  // Fetch a single Etsy listing by ID
  try {
    const res = await request.get(`/etsy/get/products/${listingid}`);
    if (res) {
      dispatch(loadingAction(false));
      dispatch(setEtsyListing(res.data));
    }
  } catch (e) {
    dispatch(loadingAction(false));

    toastify("error", e.response.data.message);
  }
};


export const resyncEtsyListings = async (dispatch) => {
  dispatch(setEtsyResyncLoading(true));

  try {
    const lastOffset = parseInt(
      localStorage.getItem("lastEtsyOffset") || "0",
      10,
    );
    const limit = 100;

    const res = await request.get(
      `/etsy/get/products?limit=${limit}&&offset=${lastOffset}`,
    );

    if (res && res.data && res.data.data && res.data.data.listings) {
      const listings = res.data.data.listings;

      if (listings.length > 0) {
        const sorted = listings.sort((a, b) => {
          const aOwned = a.listing_linked === true;
          const bOwned = b.listing_linked === true;

          if (aOwned && !bOwned) return 1;
          if (!aOwned && bOwned) return -1;
          return 0;
        });

        dispatch(appendEtsyListings(sorted));

        const newOffset = lastOffset + 1;
        dispatch(setLastEtsyOffset(newOffset));
        localStorage.setItem("lastEtsyOffset", newOffset.toString());

        toastify(
          "success",
          `Successfully synced ${listings.length} new Etsy listings`,
        );
      } else {
        toastify("info", "No new Etsy listings found.");
      }
    } else {
      toastify("info", "No new Etsy listings found.");
    }

    dispatch(setEtsyResyncLoading(false));
  } catch (e) {
    dispatch(setEtsyResyncLoading(false));
    toastify("error", e.response.data.message);
  }
};

export const linkProductByListingID = async (
  dispatch,
  formData,
  productID,
  options = {},
  etsyListings,
  currentStatus = null,
  currentStatusListings = null,
) => {
  const {
    showSuccessMessage = true,
    closeModal = true,
    refreshProductList = true,
    refreshProductDetail = true,
    onSuccess,
    onError,
  } = options;

  try {
    const res = await request.post("/products/link/etsy", formData);
    if (res) {
      if (showSuccessMessage) {
        toastify("success", res.data.message);
      }

      if (closeModal) {
        dispatch(setModalState(undefined));
      }

      if (refreshProductList) {
        getProductList(dispatch, 1, 25);
      }

      if (refreshProductDetail) {
        getProductsDetail(dispatch, productID);
      }

      // Call custom success callback if provided
      if (onSuccess) {
        onSuccess(res.data);
      }

      // Update Etsy listing status
      const listingId = formData.get("listing_id");
      if (listingId && etsyListings) {
        updateEtsyListing(
          dispatch,
          listingId,
          etsyListings,
          currentStatus,
          currentStatusListings,
        );
      }
    }
  } catch (e) {
    const errorMessage =
      e.response?.data?.message || "Failed to link Etsy product";
    toastify("error", errorMessage);

    // Call custom error callback if provided
    if (onError) {
      onError(e);
    }
  }
};

export const updateEtsyListing = async (
  dispatch,
  listingId,
  etsyListings,
  currentStatus = null,
  currentStatusListings = null,
) => {
  const payload = {
    listing_id: listingId,
  };

  try {
    const res = await request.post("etsy/listing/status", payload);

    if (res) {
      const newObject = res.data.data;

      // Get the correct status-specific state based on currentStatus
      let currentListings = [];
      let updateAction = null;

      if (currentStatus && currentStatusListings) {
        const statusMap = {
          1: "active",
          2: "inactive",
          3: "draft",
        };

        const statusKey = statusMap[currentStatus];
        if (statusKey) {
          // Use the current status-specific listings passed as parameter
          currentListings = currentStatusListings || [];

          // Map status to the appropriate action
          switch (statusKey) {
            case "active":
              updateAction = setEtsyListingsActive;
              break;
            case "inactive":
              updateAction = setEtsyListingsInactive;
              break;
            case "draft":
              updateAction = setEtsyListingsDraft;
              break;
            default:
              break;
          }
        }
      } else {
        // Fallback to legacy state if no currentStatus provided
        currentListings = etsyListings || [];
      }

      // Find the index of the object with matching listing_id
      const index = currentListings?.findIndex(
        (listing) => Number(listing.listing_id) === Number(listingId),
      );

      if (index === -1) {
        // No matching listing found, return original array
        console.warn(`No listing found with listing_id: ${listingId}`);
        return currentListings;
      }

      // Create a new array with the updated object
      const updatedListings = [...currentListings];
      updatedListings[index] = { ...newObject };

      // Update legacy Redux state with the updated listings
      dispatch(setEtsyListings(updatedListings));

      // Update the specific status state if we have the correct action
      if (updateAction) {
        dispatch(updateAction(updatedListings));
      } else {
        // If no specific action, update all status-specific states for backward compatibility
        dispatch(setEtsyListingsActive(updatedListings));
        dispatch(setEtsyListingsInactive(updatedListings));
        dispatch(setEtsyListingsDraft(updatedListings));
      }
    }
  } catch (e) {
    // toastify("error", e.response.data.message);
    dispatch(loadingBtnAction(false));
  }
};

export const loadEtsyListingsByStatus = async (dispatch, status) => {
  const statusConfig = {
    active: {
      loadingAction: setEtsyListingsActiveLoading,
      dataAction: setEtsyListingsActive,
      errorMsg: "active",
    },
    inactive: {
      loadingAction: setEtsyListingsInactiveLoading,
      dataAction: setEtsyListingsInactive,
      errorMsg: "inactive",
    },
    draft: {
      loadingAction: setEtsyListingsDraftLoading,
      dataAction: setEtsyListingsDraft,
      errorMsg: "draft",
    },
  };

  const config = statusConfig[status];
  if (!config) {
    console.error(`Invalid status: ${status}`);
    return;
  }

  dispatch(config.loadingAction(true));

  try {
    const res = await request.get(`/etsy/get/products?state=${status}`);

    if (res && res.data && res.data.data && res.data.data.listings) {
      const listings = res.data.data.listings;

      // Sort listings (owned listings first)
      const sorted = listings.sort((a, b) => {
        const aOwned = a.listing_linked === true;
        const bOwned = b.listing_linked === true;

        if (aOwned && !bOwned) return 1;
        if (!aOwned && bOwned) return -1;
        return 0;
      });

      dispatch(config.dataAction(sorted));
    }

    dispatch(config.loadingAction(false));
  } catch (e) {
    dispatch(config.loadingAction(false));
    // toastify("error", e.response.data.message);
  }
};

export const loadEtsyListingsInactive = async (dispatch) => {
  return loadEtsyListingsByStatus(dispatch, "inactive");
};

export const loadEtsyListingsDraft = async (dispatch) => {
  return loadEtsyListingsByStatus(dispatch, "draft");
};

// Optimized single function for resyncing Etsy listings by status
export const resyncEtsyListingsByStatus = async (dispatch, status) => {
  // Map status to Redux actions
  const statusConfig = {
    active: {
      loadingAction: setEtsyResyncActiveLoading,
      dataAction: setEtsyListingsActive,
      errorMsg: "active",
    },
    inactive: {
      loadingAction: setEtsyResyncInactiveLoading,
      dataAction: setEtsyListingsInactive,
      errorMsg: "inactive",
    },
    draft: {
      loadingAction: setEtsyResyncDraftLoading,
      dataAction: setEtsyListingsDraft,
      errorMsg: "draft",
    },
  };

  const config = statusConfig[status];
  if (!config) {
    console.error(`Invalid status: ${status}`);
    return;
  }

  dispatch(config.loadingAction(true));

  try {
    const res = await request.get(`/etsy/get/products?state=${status}`);

    if (res && res.data && res.data.data && res.data.data.listings) {
      const listings = res.data.data.listings;

      if (listings.length > 0) {
        const sorted = listings.sort((a, b) => {
          const aOwned = a.listing_linked === true;
          const bOwned = b.listing_linked === true;

          if (aOwned && !bOwned) return 1;
          if (!aOwned && bOwned) return -1;
          return 0;
        });

        dispatch(config.dataAction(sorted));
      }
    }
    dispatch(config.loadingAction(false));
  } catch (e) {
    toastify("error", e.response.data.message);
    dispatch(config.loadingAction(false));
  }
};

export const resyncEtsyListingsInactive = async (dispatch) => {
  return resyncEtsyListingsByStatus(dispatch, "inactive");
};

export const resyncEtsyListingsDraft = async (dispatch) => {
  return resyncEtsyListingsByStatus(dispatch, "draft");
};

// Function to fetch all products on login
export const getAllProducts = async (dispatch) => {
  dispatch(loadingAction(true));

  try {
    const res = await request.get(`/products`);
    if (res) {
      dispatch(loadingAction(false));
      dispatch(setProductsList(res.data.data.data));
      dispatch(setProductsMeta(res.data.data.meta));
      dispatch(setProductSearched(false));
    }
  } catch (e) {
    dispatch(loadingAction(false));

    if (e?.response?.status === 404) {
      dispatch(setProductsList([]));
    } else {
      console.error("Error fetching all products:", e);
    }
  }
};
